class SDPresentationTimeline {
  constructor(root) {
    this.root = root;
    this.timeline = root.querySelector('[data-sd-presentation-timeline]');
    this.dots = root.querySelectorAll('[data-sd-presentation-dot]');
    this.connectors = root.querySelectorAll('[data-sd-presentation-connector]');
    this.connectorFills = root.querySelectorAll('[data-sd-presentation-connector-fill]');
    this.totalLength = 0;
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.resizeObserver = null;
  }

  getTriggerLine() {
    return window.innerHeight * 0.55;
  }

  measure() {
    this.totalLength = Array.from(this.connectors).reduce(
      (sum, connector) => sum + connector.offsetHeight,
      0
    );
  }

  updateFill() {
    if (!this.dots.length) return;

    const firstCenter =
      this.dots[0].getBoundingClientRect().top + this.dots[0].offsetHeight / 2;
    const lastCenter =
      this.dots[this.dots.length - 1].getBoundingClientRect().top +
      this.dots[this.dots.length - 1].offsetHeight / 2;
    const triggerLine = this.getTriggerLine();

    let progress = 0;

    if (lastCenter <= triggerLine) {
      progress = 1;
    } else if (firstCenter >= triggerLine) {
      progress = 0;
    } else {
      progress = (triggerLine - firstCenter) / (lastCenter - firstCenter);
      progress = Math.min(1, Math.max(0, progress));
    }

    const fillHeight = progress * this.totalLength;
    let traveled = 0;

    this.connectorFills.forEach((fill, index) => {
      const connector = this.connectors[index];
      const segmentHeight = connector?.offsetHeight || 0;
      const segmentStart = traveled;
      const segmentFill = Math.min(
        segmentHeight,
        Math.max(0, fillHeight - segmentStart)
      );

      fill.style.height = `${segmentFill}px`;
      traveled += segmentHeight;
    });

    this.dots.forEach((dot, index) => {
      const stepCount = this.dots.length - 1;

      if (stepCount === 0) {
        dot.classList.toggle('is-active', progress > 0);
        return;
      }

      const threshold = index / stepCount;
      dot.classList.toggle('is-active', progress >= threshold - 0.02);
    });
  }

  refresh() {
    this.measure();
    this.updateFill();
  }

  onScroll() {
    if (this.ticking) return;

    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.refresh();
      this.ticking = false;
    });
  }

  onResize() {
    this.refresh();
  }

  enable() {
    if (!this.timeline) return;

    window.requestAnimationFrame(() => {
      this.refresh();
    });

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.timeline);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.refresh());
    }
  }

  disable() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.connectorFills.forEach((fill) => {
      fill.style.height = '0';
    });

    this.dots.forEach((dot) => dot.classList.remove('is-active'));
  }
}

class SDPresentation extends HTMLElement {
  constructor() {
    super();
    this.timeline = null;
  }

  connectedCallback() {
    this.timeline = new SDPresentationTimeline(this);
    this.timeline.enable();
  }

  disconnectedCallback() {
    this.timeline?.disable();
    this.timeline = null;
  }
}

if (!customElements.get('sd-presentation')) {
  customElements.define('sd-presentation', SDPresentation);
}
